import React, { useMemo, useCallback } from 'react';
import ImageModal from 'app/components/modals/ImageModal';
import { EntityStateViewName } from '@engoo/realtime-protocol/entityState';
import * as Schema from 'app/constants/schemas';
import { useChatKeyPath } from 'app/models/entityStateSync/keypath';
import { useChatImageContext } from 'app/containers/rtc/ChatImageContext';
import { EXPANDED_IMAGE_PATH_KEY } from 'app/components/chat/constants';
import { createEntityStringId } from 'app/types/entity';
import { useEntitySyncState } from 'app/containers/rtc/EntityStateSyncContext';
import { StreamPositionCodec, StreamPosition } from '@engoo/realtime-protocol/persisted';

interface IDeps {
  imagePosition: StreamPosition;
  imageEntity: Schema.Image;
  onClose: () => void;
}

const ChatImageModal: React.FunctionComponent<IDeps> = ({
  imageEntity,
  imagePosition,
  onClose,
}) => {
  const imageKeyPath = useChatKeyPath({ position: imagePosition });
  return (
    <ImageModal
      image={imageEntity}
      drawKeyPath={imageKeyPath}
      onClose={onClose}
      hasCaption={false}
    />
  );
};

const ImageModalInjectDeps: React.FunctionComponent = () => {
  const { getImageState } = useChatImageContext();
  const chatKeyPath = useChatKeyPath({ position: EXPANDED_IMAGE_PATH_KEY });
  const [keyPathState, setViewState] = useEntitySyncState(EntityStateViewName.ChatAttachment, chatKeyPath);

  const imagePosition = keyPathState?.expandedAttachmentId;

  const url = (getImageState !== undefined && imagePosition !== undefined) ?
    getImageState(imagePosition).url :
    null;

  const closeModal = useCallback(() => {
    setViewState('expandedAttachmentId', undefined);
  }, [setViewState]);

  const imageEntity = useMemo(() => {
    if (!url) {
      return null;
    }

    return new Schema.Image({
      id: createEntityStringId(),
      url,
      attribution: null,
      publicAttribution: null,
      dimensions: null,
    });
  }, [url]);

  if (!imageEntity || !StreamPositionCodec.is(imagePosition)) {
    return null;
  }

  return <ChatImageModal
    imageEntity={imageEntity}
    imagePosition={imagePosition}
    onClose={closeModal}
  />;
};

export default ImageModalInjectDeps;
